import React, { useEffect, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { ActionTypes } from "../../../../constants";

import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Container,
    Fab,
    Grid,
    Stack,
    Button,
    LinearProgress,
    Typography,
} from "@mui/material";
import ActivityFormDialog from "../ActivityForm/ActivityFormDialog";
import AlertMessage from "../../../Common/Alert/AlertMessage";
import BreadcrumbBuilder from "../BreadcrumbBuilder";
import JobPlanActivityList from "../JobPlanActivityList";
import JobPlanDetails from "./JobPlanDetails";
import JobPlanObjectiveTable from "../JobPlanObjectiveTable";
import JPAvatar from "../JPAvatar";
import Planner from "../Planner";
import RenderDate from "../../../Common/Date/RenderDate";

import InsightsIcon from "@mui/icons-material/Insights";

import AddIcon from "@mui/icons-material/Add";
import { v4 as uuidv4 } from "uuid";

import Error404 from "../../../Errors/Error404";
import Error500 from "../../../Errors/Error500";

import { getJobPlanDetails } from "../../../../actions/jpActions";
import { getJobPlan, getError } from "../../../../reducers";

const appCode = "P-JP";

function JobPlan({ jobPlan }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const handleCloseDialog = useCallback(() => setDialogOpen(false), []);
    const handleOpenDialog = useCallback(() => setDialogOpen(true), []);

    const activities = jobPlan.activities || [];
    let contractedActivityCollection = [];
    let unContractedActivityCollection = [];

    activities.forEach((activity) => {
        let durationAveragePW = 0;
        if (!activity.id) {
            activity.id = uuidv4();
        }
        if (!activity.type) {
            activity.type = {
                name: "Programmed Activity",
                code: "PA",
                contracted: true,
            };
        }
        if (typeof activity.category === "string") {
            activity.category = {
                id: uuidv4(),
                code: activity.categoryType || "X",
                name: activity.category,
            };
        }
        if (!activity.subcategory) {
            activity.subcategory = {
                id: uuidv4(),
                name: "No subcategory",
            };
        } else if (typeof activity.subcategory === "string") {
            activity.subcategory = {
                id: uuidv4(),
                name: activity.subcategory,
            };
        }
        if (!activity.duration) {
            activity.duration = 0;
        }
        if (!activity.durationUnit) {
            activity.durationUnit = "PA";
        }
        if (!activity.durationPremium) {
            activity.durationPremium = activity.paPremium || 0;
        }
        if (!activity.durationNormal) {
            activity.durationNormal = activity.paNormal || 0;
        }
        if (activity.durationNormal + activity.durationPremium === 0) {
            activity.durationNormal = activity.duration;
        }

        if (activity.scheduled?.dayOfWeek) {
            durationAveragePW =
                activity.durationNormal + activity.durationPremium;
        }

        if (activity.type.contracted) {
            if (!contractedActivityCollection[activity.type.code]) {
                contractedActivityCollection[activity.type.code] = {
                    name: activity.type.name,
                    code: activity.type.code,
                    durationAveragePW: 0,
                    activities: [],
                };
            }
            contractedActivityCollection[activity.type.code].activities[
                activity.id
            ] = activity;
            contractedActivityCollection[
                activity.type.code
            ].durationAveragePW += durationAveragePW;
        } else {
            if (!unContractedActivityCollection[activity.type.code]) {
                unContractedActivityCollection[activity.type.code] = {
                    name: activity.type.name,
                    code: activity.type.code,
                    durationAveragePW: 0,
                    activities: [],
                };
            }
            unContractedActivityCollection[activity.type.code].activities[
                activity.id
            ] = activity;
        }
    });

    const contractedActivities = Object.values(contractedActivityCollection);
    const unContractedActivities = Object.values(
        unContractedActivityCollection
    );

    const isCompleted = jobPlan.status === "signedoff";

    return (
        <Container className="content">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <BreadcrumbBuilder />
                </Grid>
                <Grid item xs={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography
                            avatar={<JPAvatar type="jobplan" />}
                            action={
                                <Stack
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    spacing={2}
                                    sx={{ padding: "8px" }}
                                >
                                    <Button
                                        variant="contained"
                                        color="neutral"
                                        disableElevation
                                        component={Link}
                                        to={`/jp/jobplans/${jobPlan.id}/insights`}
                                        startIcon={<InsightsIcon />}
                                    >
                                        Insights
                                    </Button>
                                </Stack>
                            }
                            title={
                                <Typography
                                    component="h3"
                                    className="card-header-title"
                                >
                                    {jobPlan.title || jobPlan.resourceType}
                                </Typography>
                            }
                            subheader={
                                <Typography
                                    component="span"
                                    className="card-header-subtitle"
                                >
                                    <RenderDate date={jobPlan?.effectiveDate} />
                                </Typography>
                            }
                        />
                        <CardContent>
                            <Box className="card-content">
                                <JobPlanDetails jobPlan={jobPlan} />
                            </Box>
                        </CardContent>

                        {isCompleted && (
                            <CardContent>
                                <Box className="card-content">
                                    <AlertMessage
                                        severity="success"
                                        message="This job plan has been signed off"
                                    />
                                </Box>
                            </CardContent>
                        )}
                    </Card>
                </Grid>

                <Grid item xs={12} sm={12} md={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography
                            title={
                                <Typography
                                    component="h3"
                                    className="card-header-title"
                                >
                                    Planner
                                </Typography>
                            }
                        />
                        <CardContent>
                            <Box className="card-content">
                                <Planner />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {contractedActivities?.length > 0 &&
                    contractedActivities.map((category, index) => (
                        <Grid
                            item
                            xs={12}
                            sm={12}
                            md={12}
                            key={`contracted_activities_${index}`}
                        >
                            <JobPlanActivityList
                                activities={category.activities}
                                jpId={jobPlan.id}
                                isCompleted={isCompleted}
                                contractedHours={jobPlan.contractedHours}
                                title={`${category.name} (${category.code}) - ${
                                    category.durationAveragePW > 0
                                        ? (
                                              Math.round(
                                                  category.durationAveragePW *
                                                      100
                                              ) / 100
                                          ).toFixed(3)
                                        : 0
                                }`}
                            />
                        </Grid>
                    ))}

                {unContractedActivities?.length > 0 &&
                    unContractedActivities.map((category, index) => (
                        <Grid
                            item
                            xs={12}
                            sm={12}
                            md={12}
                            key={`uncontracted_activities_${index}`}
                        >
                            <JobPlanActivityList
                                activities={category.activities}
                                contractedHours={jobPlan.contractedHours}
                                title={`${category.name} (${category.code})`}
                                jpId={jobPlan.id}
                            />
                        </Grid>
                    ))}

                <Grid item xs={12} sm={12} md={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography
                            title={
                                <Typography
                                    component="h3"
                                    className="card-header-title"
                                >
                                    On call arrangements
                                </Typography>
                            }
                        />
                        <CardContent>
                            <Box className="card-content"></Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={12} md={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography
                            title={
                                <Typography
                                    component="h3"
                                    className="card-header-title"
                                >
                                    Objectives
                                </Typography>
                            }
                        />
                        <CardContent>
                            <Box className="card-content">
                                <JobPlanObjectiveTable
                                    objectives={jobPlan.objectives}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={12} md={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography
                            title={
                                <Typography
                                    component="h3"
                                    className="card-header-title"
                                >
                                    Resources
                                </Typography>
                            }
                        />
                        <CardContent>
                            <Box className="card-content"></Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            <Fab
                color="secondary"
                aria-label="add"
                className="fab"
                onClick={handleOpenDialog}
            >
                <AddIcon />
            </Fab>

            {dialogOpen && (
                <ActivityFormDialog
                    open={dialogOpen}
                    onClose={handleCloseDialog}
                    jpId={jobPlan.id}
                    scroll="paper"
                />
            )}
        </Container>
    );
}

const JobPlanContainer = () => {
    const { id } = useParams();
    const dispatch = useDispatch();

    const jobPlan = useSelector((state) => getJobPlan(state, id));
    const error = useSelector((state) =>
        getError(state, ActionTypes.GET_JP_JOBPLAN_SUCCESS)
    );

    useEffect(() => {
        dispatch(getJobPlanDetails(appCode, id));
    }, [dispatch, id]);

    if (!!error) {
        if (error.errorCode === 404) {
            return <Error404 />;
        }
        if (error.errorCode === 500) {
            return <Error500 />;
        }
    }

    if (!jobPlan) return <LinearProgress />;

    return <JobPlan jobPlan={jobPlan} />;
};

export default JobPlanContainer;
